import { Tracing } from "@tento-core/tracing";
import { type CalendarLookup, calendarWritable, EventItemPresentation, type EventsClient } from "@tento-lona";
import type { CalendarListConfig } from "@tento-lona/platform";
import type { EventItem, Option } from "@tento-lona/sheets";

/**
 * What the user may do to one event, decided in one place for every event
 * surface (the month grid's spans and entry rows, the day columns' gesture
 * items, the details panel, the context menu). Three facts decide it:
 *
 * - **list**: whether the calendar list the event is shown through accepts
 *   writes (`CalendarListConfig.access`). A read-only list takes no write of
 *   any kind, and offers no context menu (every row of it writes or copies
 *   out of the list);
 * - **grant**: whether the account can write the event's calendar
 *   (`EventsClient.peekWritability`). Unknown (a source row not attached
 *   yet) counts as writable: the write is offered and a refusal reported;
 * - **provider**: whether the provider lets this event be edited
 *   (`EventItemPresentation.isEditable`: a writable calendar, a default
 *   event type).
 *
 * The provider's say covers the event's own fields: its time (`move`) and
 * the editable details panel (`edit`). Answering the invitation, tagging and
 * deleting need the list and the grant only: they are offered on an event
 * the provider would not let the user re-time. Copying out needs the list
 * only (the copy is written to another, writable calendar).
 */
export interface EventAccess {
  /** Drag or resize it (day columns, month spans and entry rows). */
  readonly move: boolean;
  /** Open the editable details panel (field edits, its calendar pick and Delete); else the read-only one. */
  readonly edit: boolean;
  /** Answer the invitation (details panel, context menu). */
  readonly rsvp: boolean;
  /** Tag it with a category (context menu). */
  readonly tag: boolean;
  /** Delete it from the context menu. */
  readonly delete: boolean;
  /** Copy it to another calendar (context menu). */
  readonly copy: boolean;
  /** Offer its context menu at all. */
  readonly menu: boolean;
}

/** Resolving `EventAccess`, the calendar list's half of it, and the create decision (`Create`). */
export namespace EventAccess {
  /** Whether the surface lists the event through a calendar list that accepts writes. */
  export type List = "editable" | "read-only";

  /**
   * A client's answer to "may this calendar be written?": an
   * `EventsClient.Writability`, or the month's create target. Only
   * `writable` is read.
   */
  export type GrantAnswer = Pick<EventsClient.Writability, "writable">;

  /** What `of` reads besides the event. */
  export interface Inputs {
    /** The calendar list's access (`fromListConfig`). */
    readonly list: EventAccess.List;
    /** The client's grant answer for the event's calendar. */
    readonly writes: Pick<EventsClient, "peekWritability">;
    /** The calendars, for the provider's editability. */
    readonly calendars: CalendarLookup;
  }

  /** What the user may do to `event`; see `EventAccess` for how the three facts combine. */
  export function of(event: EventItem, inputs: EventAccess.Inputs): EventAccess {
    const list = EventAccess.writableThrough(inputs.list);
    const writes = list && EventAccess.granted(event, inputs.writes);
    const edit = writes && EventItemPresentation.isEditable(event, inputs.calendars);
    return { move: edit, edit, rsvp: writes, tag: writes, delete: writes, copy: list, menu: list };
  }

  /**
   * Which fact withholds `edit` from an event, for a surface that tells the
   * user why nothing can be changed: the list (`"list"`), the calendar's own
   * role at its provider (`"calendar"`: the account holds it as a reader),
   * the account's grant (`"grant"`: a connection without write permission),
   * or the provider's rule for this event (`"event"`: its type, its
   * organizer, or a calendar not in the loaded list).
   */
  export type ReadOnlyCause = "list" | "calendar" | "grant" | "event";

  /** Why `of` withholds `edit` from `event` (`ReadOnlyCause`); null when it grants it. */
  export function readOnlyCause(event: EventItem, inputs: EventAccess.Inputs): Option<EventAccess.ReadOnlyCause> {
    if (EventAccess.of(event, inputs).edit) return null;
    if (!EventAccess.writableThrough(inputs.list)) return "list";
    const display = EventItemPresentation.acal(event, inputs.calendars)?.[1];
    if (display != null && !calendarWritable(display)) return "calendar";
    if (!EventAccess.granted(event, inputs.writes)) return "grant";
    return "event";
  }

  /**
   * The grant fact alone: whether the account may write `event`'s calendar,
   * as far as the client knows (unknown counts as writable). `of` reads it;
   * a surface reads `of`, and only refusal reporting reads this directly.
   */
  export function granted(event: EventItem, writes: Pick<EventsClient, "peekWritability">): boolean {
    return EventAccess.allows(writes.peekWritability(event));
  }

  /**
   * The grant rule, for an event (`granted`) and for a create
   * (`Create.accepts`) alike: a client answer allows a write unless
   * it says the calendar is read-only. An unknown answer (`null`: the row
   * is not attached yet) allows it, and a refusal is then reported.
   */
  export function allows(answer: Option<EventAccess.GrantAnswer>): boolean {
    return answer?.writable !== false;
  }

  /**
   * The list half of `of`: whether a list with this access lets its events
   * be written at all (moved, resized, offered the writing menu), and lets a
   * surface shown through it offer creating (`Create.offered`). A gesture
   * that only knows the opening list asks this before it starts.
   */
  export function writableThrough(list: EventAccess.List): boolean {
    return list === "editable";
  }

  /**
   * The list access of a surface only a writing list reaches. The base event
   * item delegate serves writing lists: a read-only list wraps it in
   * `readOnlyEventItemDelegate`, which overrides every member that reads this
   * (`isEditable`, `showDetails`, `showContextMenu`). The all-day strip has
   * no delegate: it carries the opening list's access on `AllDayEventInfo.list`,
   * and its move, resize and context-menu gestures refuse a list for which
   * `writableThrough` is false, so its menu opens only for a writing list; a
   * create ghost exists only where `Create.offered` held. A host that audits its
   * code for list accesses can allow this name and refuse a bare `"editable"`
   * literal.
   */
  export const WRITING_LIST: EventAccess.List = "editable";

  /**
   * The list access of events no calendar list lists (a flight row's events):
   * no list restricts them, so the grant and the provider decide.
   */
  export const UNLISTED: EventAccess.List = "editable";

  /**
   * A calendar list's `access` setting as the list half of `Inputs`.
   * Exhaustive over `access`: a state this code does not know fails closed
   * (read-only) instead of open.
   */
  export function fromListConfig(access: CalendarListConfig["access"]): EventAccess.List {
    switch (access) {
      case "editable":
        return "editable";
      case "read-only":
        return "read-only";
      default: {
        const unknown: never = access;
        Tracing.err("ui")("calendar/list-access-unknown", { access: String(unknown) });
        return "read-only";
      }
    }
  }

  /**
   * Where the user may create an event: the create half of the access
   * decision. It is named in the `EventAccess` namespace but is not part of
   * an `EventAccess` value, because a create has no event yet. It is decided
   * per calendar, from two of `EventAccess`'s three facts:
   *
   * - **list** (`offered`): a surface offers the create gesture only through
   *   a calendar list that accepts writes. The month grid reads it in
   *   `EventsSource.createTarget` (`onCreateStart` and the create popovers),
   *   the day columns in `CalendarListAliasPlugin.subcolumnAccess`;
   * - **grant** (`accepts`): the target calendar's answer, with the rule
   *   `EventAccess.allows` (unknown counts as writable). The create form's
   *   calendar pickers ask `EventsClient.peekCalendarWritability` and leave
   *   out a calendar it answers read-only (`offeredCalendars`). The month
   *   grid asks the SDK's create target (`CalendarMonthRead.peekCreateTarget`),
   *   whose `writable` is the same fact for the calendar the month writes to;
   * - **provider**: does not apply. It says whether an existing event may be
   *   re-timed or edited, and there is none.
   *
   * A create that the client or the server still refuses is reported to the
   * user through `EventWriteRefusal.CalendarReport`.
   */
  export namespace Create {
    /** The list half: whether a surface shown through a list with this access offers creating (`writableThrough`). */
    export function offered(list: EventAccess.List): boolean {
      return EventAccess.writableThrough(list);
    }

    /** The grant half: whether a calendar with this client answer takes a new event (`allows`). */
    export function accepts(answer: Option<EventAccess.GrantAnswer>): boolean {
      return EventAccess.allows(answer);
    }
  }
}
